import { useNavigate, useSearch } from "@tanstack/react-router";

import { FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";
import { TIME_RANGE_OPTIONS, effectiveGrain, parseRange, type RangeValue } from "@/lib/time-range";

import type { CostStackMode } from "./api";
import type { Grain } from "./format";

/**
 * The view controls, and the URL they live in.
 *
 * State is in the URL rather than `useState` on purpose: "look at the weekly
 * per-unit stack for EU" has to be a link a person can paste to a teammate. A
 * `useState` version of this screen turns every handover into a paragraph of
 * instructions about which dropdowns to set.
 *
 * The controls themselves are the shell-owned `FilterBar` (I-7) - this module
 * draws no select of its own. `cost_basis` lives IN the CostStackPanel's own
 * header (W5). The page Period drives every chart, the cost stack included
 * (bug 3f30402f-c6c9-81e8) - the panel no longer has a grain control of its own.
 */
export const CONTROL_SPECS: FilterSpec[] = [
  {
    name: "range",
    label: "Range",
    kind: "choice",
    options: TIME_RANGE_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
  },
  {
    name: "period",
    label: "Period",
    kind: "choice",
    options: [
      { value: "daily", label: "Daily" },
      { value: "weekly", label: "Weekly" },
      { value: "monthly", label: "Monthly" },
    ],
  },
];

export type Controls = {
  range: RangeValue;
  grain: Grain;
  costMode: CostStackMode;
  values: FilterValues;
  onChange: (next: FilterValues) => void;
};

/**
 * `monthly` is the default trend grain because it is the only one the producer
 * guarantees: daily and weekly carry real data only once the cron has
 * backfilled them, and defaulting to an empty chart would read as an outage on
 * a working dashboard.
 */
export function useControls(): Controls {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);

  const range = parseRange(values.range as string | null | undefined);
  const rawGrain = values.period as string | null | undefined;
  const grain: Grain = effectiveGrain(range, rawGrain) as Grain;
  const costMode: CostStackMode = values.cost_basis === "pct_of_sales" ? "pct_of_sales" : "per_unit";

  return {
    range,
    grain,
    costMode,
    values,
    onChange: (next) => {
      void navigate({ to: ".", search: applyFilters(search, next), replace: true });
    },
  };
}

export function YieldControls({ controls }: { controls: Controls }) {
  // Show the effective choice rather than "Any" when the URL carries no param.
  const values = {
    ...controls.values,
    range: controls.range,
    period: controls.grain,
  };
  return (
    <FilterBar layout="segmented" filters={CONTROL_SPECS} values={values} onChange={controls.onChange} />
  );
}
